import React, { useMemo } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { BsFillTrash3Fill as DeleteIcon } from 'react-icons/bs';

import SVButton from 'components/elements/SVButton';
import Table from 'components/elements/Table';

interface FileItem {
  id: string | number;
  fileName: string;
  [key: string]: any;
}

interface FilesData {
  items: FileItem[];
  total: number;
  limit: number;
}

interface Files {
  isLoading: boolean;
  error?: string;
  data: FilesData;
}

interface FileTableProps {
  onFileClick: (file: FileItem) => void;
  onFileDelete: (id: string | number) => void;
  handleSortChange: (order: string, orderDirection: string) => void;
  handlePageChange: (page: number) => void;
  files: Files;
  page: number;
  sort: string;
  sortOrder: string;
}

const FileTable: React.FC<FileTableProps> = ({
  onFileClick,
  onFileDelete,
  page,
  handlePageChange,
  sort,
  sortOrder,
  handleSortChange,
  files: { isLoading, error, data },
}) => {
  const fileClickButton = React.useCallback(
    (fields: FileItem) => (
      <Button
        variant="link"
        onClick={() => {
          onFileClick(fields);
        }}
      >
        {fields.fileName}
      </Button>
    ),
    [onFileClick],
  );

  const fileDeleteButton = React.useCallback(
    (fields: FileItem) => (
      <div style={{ textAlign: 'right' }}>
        <SVButton
          size="sm"
          variant="danger"
          onClick={() => onFileDelete(fields.id)}
        >
          <DeleteIcon />
        </SVButton>
      </div>
    ),
    [onFileDelete],
  );

  const columns = useMemo(
    () => [
      {
        id: 'name',
        label: 'Document Name',
        override: fileClickButton,
      },
      {
        id: 'delete',
        label: '',
        override: fileDeleteButton,
      },
    ],
    [fileClickButton, fileDeleteButton],
  );

  if (error) return <Alert variant="danger">{error}</Alert>;

  const { total, limit } = data;

  return (
    <Table
      columns={columns}
      items={data?.items ?? []}
      order={sort}
      orderDirection={sortOrder === 'asc' || sortOrder === 'desc' ? sortOrder : undefined}
      pagination={{ total, limit, page }}
      handlePageChange={handlePageChange}
      handleOrderChange={(order: string) => handleSortChange(order, sortOrder)}
      isLoading={isLoading}
    />
  );
};

export default FileTable;
